import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { KAL_DIALOG_DATA, KalDialogRef, KalDialogService } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import {
    ArrayOfModeleAdresseBaseModel,
    CourrierDto, InitiModelImpressionDto,
    ModeleAdresseBaseModel,
    MutationGetListeModelesAdresseArgs,
    MutationInitialisationModeleImpressionArgs,
    MutationSaveModeleImpressionArgs,
    ParametreImpressionDto
} from 'src/graphql/generated-types';
import { tap } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SharedMailComponent } from 'src/app/_shared/components/mail/shared-mail/shared-mail.component';
import { Store } from '@ngxs/store';
import { FormValidator } from 'src/app/validators/form-validator';
import { TypeReglement } from 'src/app/board/activity/activity.constants';
import { UserState } from 'src/app/_user/store/user/user.state';
import { CodesReports } from 'src/environments/codes-reports';
import { ViewerDocument } from 'src/app/_shared/components/viewer-document/viewer-document.component';
import { forkJoin, Observable } from 'rxjs';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { ActivityPrintMailService } from 'src/app/_core/services/activity-print-mail.service';

export interface SharedChequeLetterDialogData {
    isToSupplier: boolean;
    context: TypeReglement;
    checkDepositsId: number[];
    document?: ViewerDocument;
    accepted?: boolean;
}

const MAX_LENGTH_LABEL = 50;
const MAX_LENGTH_SUBJECT = 50;


@Component({
    selector: 'app-shared-cheque-letter-dialog',
    templateUrl: './shared-cheque-letter-dialog.component.html',
    styleUrls: ['./shared-cheque-letter-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedChequeLetterDialogComponent extends SharedMailComponent implements OnInit {

    lettersList: CourrierDto[] = [];
    parametersList: ParametreImpressionDto[] = [];

    addressModelsList: ModeleAdresseBaseModel[] = [];
    form: FormGroup;

    constructor(protected readonly cdr: ChangeDetectorRef,
                protected readonly fb: FormBuilder,
                protected readonly store: Store,
                protected readonly graphqlService: GraphqlService,
                protected readonly dialogService: KalDialogService,
                private readonly printService: ActivityPrintMailService,
                private readonly dialogRef: KalDialogRef<SharedChequeLetterDialogComponent>,
                @Inject(KAL_DIALOG_DATA) private data: SharedChequeLetterDialogData) {
        super(fb, store, graphqlService, cdr, dialogService);
    }

    private initPrintModel$(): Observable<InitiModelImpressionDto> {
        return this.graphqlService.mutateOne<'InitialisationModeleImpression', MutationInitialisationModeleImpressionArgs>({
            mutation: gql`
                mutation InitialisationModeleImpression($contexte: ContextInfosInput, $Criteres: CriteresCourrierDTOInput, $CodeReport: String) {
                    InitialisationModeleImpression(contexte: $contexte, Criteres: $Criteres, CodeReport: $CodeReport) {
                        ListeCourrier {
                            CourrierDTO {
                                Activite {
                                    Id
                                }
                                Corps
                                DateDerEnvoi
                                Id
                                Intro
                                IsAdresseCEVisible
                                IsCloture
                                IsLogoVisible
                                IsSignatureCEVisible
                                Libelle
                                ModeleAdresse {
                                    Id,
                                    Libelle
                                }
                                Objet
                                PiedDePage
                                BasDePage
                                SignatureCE
                                Titre
                                TypeDestinataire
                            }
                        }
                        ListeParametresImpression {
                            ParametreImpressionDTO {
                                IdParametre
                                CodeParametre
                            }
                        }
                    }
                }
            `,
            variables: {
                CodeReport: 'LCHQ',
                Criteres: {
                    CodeTypeReport: 'LCH',
                    IsClotured: false,
                    TakeModeleAdresse: true,
                    TakeReport: true,
                    TypeDestinataire: this.data.isToSupplier ? 'F' : 'C',
                }
            }
        }).pipe(
            tap(InitialisationModeleImpression => {
                this.lettersList = InitialisationModeleImpression.ListeCourrier?.CourrierDTO || [];
                this.parametersList = InitialisationModeleImpression.ListeParametresImpression?.ParametreImpressionDTO || [];
            })
        );
    }

    getAdressModelList$(): Observable<ArrayOfModeleAdresseBaseModel> {
        return this.graphqlService.mutateOne<'GetListeModelesAdresse', MutationGetListeModelesAdresseArgs>({
            mutation: gql`
                mutation GetListeModelesAdresse(
                    $contexte: ContextInfosInput,
                ) {
                    GetListeModelesAdresse(
                        contexte: $contexte
                        criteres: {
                            IsClos: false
                        }
                    ) {
                        ModeleAdresseBaseModel {
                            IdModeleAdresse
                            LibelleModeleAdresse
                        }
                    }
                }
            `
        }).pipe(
            tap(GetListeModelesAdresse => this.addressModelsList = GetListeModelesAdresse?.ModeleAdresseBaseModel || [])
        );
    }

    saveModel(): void {
        this.watch$ = this.graphqlService.mutateOne<'SaveModeleImpression', MutationSaveModeleImpressionArgs>({
            mutation: gql`
                mutation SaveModeleImpression($contexte: ContextInfosInput, $SaveModel: SaveModelImpressionDTOInput) {
                    SaveModeleImpression(contexte: $contexte, SaveModel: $SaveModel)
                }
            `,
            variables: {
                SaveModel: {
                    CodeReport: CodesReports.LettreCheque,
                    ListeParametresImpression: {
                        ParametreImpressionDTO: this.parametersList.map(({IdParametre, CodeParametre}) => ({
                            IdParametre,
                            ValeurParametre: this.fillParameter(CodeParametre)
                        }))
                    },
                    Courrier: {
                        ...this.form.value,
                        ModeleAdresse: {
                            Id: this.form.value.ModeleAdresse?.IdModeleAdresse,
                            Libelle: this.form.value.ModeleAdresse?.LibelleModeleAdresse
                        }
                    }
                }
            }
        }).pipe(
            tap(printId => {
                const document: ViewerDocument = this.printService.getSupplierViewerDocumentsListByPrintInfos({chequeLetterPrintId: printId}, {chequeLetter: true})[0];
                this.dialogRef.close({document, accepted: true});
            })
        );
    }

    close(): void {
        this.dialogRef.close();
    }

    private fillParameter(code: string): string {
        switch (code) {
            case 'LCHQ_LISTE_ID':
                return this.data.checkDepositsId.join(',');
            case 'LCHQ_CTXT':
                return this.getContextParameter();
            case 'LCHQ_PV_ID':
                return '' + this.store.selectSnapshot(UserState.userContext).CurrentPV;
        }
    }

    /**
     * "AC" pour les avoirs bénéficiaires,
     * "ACF" pour les acomptes fournisseurs
     * "FF" pour les factures fournisseurs
     */
    private getContextParameter(): string {
        switch (this.data.context) {
            case TypeReglement.AcompteClient:
            case TypeReglement.RemboursementAvoirClient:
                return 'AC';
            case TypeReglement.AcompteFournisseur:
                return 'ACF';
            case TypeReglement.Fournisseur:
                return 'FF';
        }
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            Id: [null, Validators.required],
            Libelle: ['', [FormValidator.isNotNull, Validators.maxLength(MAX_LENGTH_LABEL)]],
            Objet: ['', [FormValidator.isNotNull, Validators.maxLength(MAX_LENGTH_SUBJECT)]],
            Corps: ['', FormValidator.isNotNull],
            IsLogoVisible: [false],
            IsAdresseCEVisible: [false],
            ModeleAdresse: ['', FormValidator.isNotNull],
            SignatureCE: [''],
            PiedDePage: [''],
            BasDePage: [''],
        });

        this.loading = true;

        this.watch$ = forkJoin([
            this.initPrintModel$(),
            this.getAdressModelList$()
        ]).pipe(
            wheneverEnd( () => {
                if (this.lettersList.length === 1) {
                    this.form.patchValue(this.lettersList[0], {emitEvent: false});
                }
                this.loading = false;
            })
        );

        this.watch$ = this.form.get('Id').valueChanges.pipe(
            tap(selectedId => this.form.patchValue(this.lettersList.find(({Id}) => Id === selectedId), {emitEvent: false})),
        );
    }

}
